import React, { createContext, useContext, ReactNode } from 'react';
import { PaginatedData } from '../types/pagination';

interface PaginationContextType<T> {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  paginatedData: PaginatedData<T>;
  totalPages: number;
}

const PaginationContext = createContext<PaginationContextType<any> | undefined>(
  undefined
);

interface PaginationProviderProps<T> {
  children: ReactNode;
  data: T[];
  initialPageSize?: number;
}

export function PaginationProvider<T extends { id: string }>({
  children,
  data,
  initialPageSize = 3,
}: PaginationProviderProps<T>) {
  const [currentPage, setCurrentPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(initialPageSize);

  const totalPages = Math.ceil(data.length / pageSize);

  const paginatedData: PaginatedData<T> = {
    items: data.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    currentPage,
    totalPages,
  };

  const value: PaginationContextType<T> = {
    currentPage,
    setCurrentPage,
    pageSize,
    setPageSize,
    totalPages,
    paginatedData,
  };

  return (
    <PaginationContext.Provider value={value}>
      {children}
    </PaginationContext.Provider>
  );
}

export const usePagination = <T extends { id: string }>() => {
  const context = useContext(PaginationContext);
  if (!context) {
    throw new Error('usePagination must be used within a PaginationProvider');
  }
  return context as PaginationContextType<T>;
};
